/**
 * "Back to where you were" pill (SYR-68).
 *
 * Renders at the top of the page content (above the page title) only when a
 * return point is set — i.e. the user deep-linked out of the Briefing. Clicking
 * it restores the origin route, sub-tab and scroll position. Matches the
 * hand-off `.nav-back` light pill.
 */

import React from 'react';
import { LeftOutlined } from '@ant-design/icons';
import { FormattedMessage } from 'react-intl';
import { useNavReturn } from './NavReturnContext';
import { THEME } from '../config/theme';

export const BackBar: React.FunctionComponent = () => {
  const { returnPoint, goBack } = useNavReturn();
  if (!returnPoint) return null;

  return (
    <div
      style={{
        // Float top-left just below the 65px sticky top bar so it stays reachable
        // without scrolling back up. fit-content width keeps the rest of the row
        // clickable as content scrolls underneath.
        position: 'sticky',
        top: 77,
        // Above page content — incl. the embedded Benchmarking page's own sticky
        // header (z-index 100) — so the pill always floats clear. It sits at
        // top:77 (below the 65px marketer top bar), so no visual clash there.
        zIndex: 101,
        width: 'fit-content',
        marginBottom: 18,
      }}
    >
      <button
        type="button"
        onClick={goBack}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          fontFamily: 'inherit',
          fontSize: 13.5,
          fontWeight: 600,
          color: THEME.textSecondary,
          background: THEME.bgCard,
          border: `1px solid ${THEME.border}`,
          borderRadius: 999,
          padding: '8px 16px 8px 12px',
          cursor: 'pointer',
          boxShadow: THEME.cardShadow,
        }}
      >
        <LeftOutlined style={{ fontSize: 14 }} />
        <span>
          <FormattedMessage
            id="marketerView.backBar.label"
            defaultMessage="Back to {label}"
            values={{
              label: (
                <b style={{ color: THEME.textBase, fontWeight: 700 }}>
                  {returnPoint.label}
                </b>
              ),
            }}
          />
        </span>
      </button>
    </div>
  );
};
